import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pencil, Users, Key, UserPlus, Trash2, Copy, Check, RefreshCw, Search, Link, ShoppingBag, Ban, CheckCircle2 } from "lucide-react";
import { useLocation } from "wouter";
import type { Client, Seller } from "@shared/schema";
import type { AdminSession } from "@/components/layouts/admin-layout";

function generatePassword(length = 8): string {
  const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(array[i] % chars.length);
  }
  return password;
}

const clientFormSchema = z.object({
  name: z.string().min(1, "Namn krävs"),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  notes: z.string().optional(),
  hasRut: z.boolean().default(false),
  rutDetails: z.string().optional(),
  orgNumber: z.string().optional(),
  invoiceCadence: z.enum(["weekly", "monthly"]).optional(),
  invoiceWeekday: z.coerce.number().min(0).max(6).optional(),
  invoiceTime: z.string().optional(),
  priceListId: z.string().nullable().optional(),
  sellerId: z.string().nullable().optional(),
  communicationPreference: z.enum(["email", "whatsapp", "sms", "none"]).default("email"),
});

type ClientFormData = z.infer<typeof clientFormSchema>;

const credentialsFormSchema = z.object({
  username: z.string().min(3, "Användarnamn måste vara minst 3 tecken"),
  password: z.string().min(6, "Lösenord måste vara minst 6 tecken"),
});

type CredentialsFormData = z.infer<typeof credentialsFormSchema>;

export default function AdminClients() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [credentialsClient, setCredentialsClient] = useState<Client | null>(null);
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [deletingClientId, setDeletingClientId] = useState<string | null>(null);
  const [savedCredentials, setSavedCredentials] = useState<{ username: string; password: string; clientName: string } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [existingUsername, setExistingUsername] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [regLinkDialogOpen, setRegLinkDialogOpen] = useState(false);
  const [regLinkCopied, setRegLinkCopied] = useState(false);

  const { data: currentAdmin } = useQuery<AdminSession | null>({
    queryKey: ["/api/admin-auth/me"],
  });
  const isSuperAdmin = currentAdmin?.isSuperAdmin ?? false;

  const { data: clients, isLoading } = useQuery<Client[]>({
    queryKey: ["/api/admin/clients"],
  });

  const { data: priceListOptions } = useQuery<{ id: string; name: string; isDefault: boolean }[]>({
    queryKey: ["/api/admin/price-lists"],
  });

  const { data: sellers } = useQuery<Seller[]>({
    queryKey: ["/api/admin/sellers"],
  });

  const filteredClients = clients?.filter((client) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      client.name.toLowerCase().includes(q) ||
      (client.address || "").toLowerCase().includes(q) ||
      (client.phone || "").toLowerCase().includes(q) ||
      (client.email || "").toLowerCase().includes(q) ||
      (client.orgNumber || "").toLowerCase().includes(q)
    );
  });

  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      name: "",
      address: "",
      phone: "",
      email: "",
      notes: "",
      hasRut: false,
      rutDetails: "",
      orgNumber: "",
      invoiceCadence: undefined,
      invoiceWeekday: undefined,
      invoiceTime: "",
      priceListId: null,
      sellerId: null,
      communicationPreference: "email",
    },
  });

  const credentialsForm = useForm<CredentialsFormData>({
    resolver: zodResolver(credentialsFormSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ClientFormData & { id: string }) =>
      apiRequest("PATCH", `/api/admin/clients/${data.id}`, data),
    onSuccess: () => {
      toast({ title: "Kund uppdaterad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/clients"] });
      setDialogOpen(false);
      setEditingClient(null);
      form.reset();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte uppdatera kund.", variant: "destructive" });
    },
  });

  const credentialsMutation = useMutation({
    mutationFn: async (data: CredentialsFormData & { clientId: string }) => {
      const response = await apiRequest("POST", `/api/admin/clients/${data.clientId}/credentials`, data);
      return { ...response, submittedPassword: data.password };
    },
    onSuccess: (data: { username?: string; submittedPassword: string }) => {
      setSavedCredentials({
        username: data.username || credentialsForm.getValues("username"),
        password: data.submittedPassword,
        clientName: credentialsClient?.name || "",
      });
      setCredentialsDialogOpen(false);
      setCredentialsClient(null);
      credentialsForm.reset();
    },
    onError: async (error: any) => {
      const message = error?.message || "Kunde inte spara inloggningsuppgifter.";
      toast({ title: "Fel", description: message, variant: "destructive" });
    },
  });

  const newClientForm = useForm<ClientFormData>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      name: "",
      address: "",
      phone: "",
      email: "",
      notes: "",
      hasRut: false,
      rutDetails: "",
      orgNumber: "",
      invoiceCadence: undefined,
      invoiceWeekday: undefined,
      invoiceTime: "",
      priceListId: null,
      sellerId: null,
      communicationPreference: "email",
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: ClientFormData) =>
      apiRequest("POST", `/api/admin/clients`, { ...data, userId: `manual-${Date.now()}` }),
    onSuccess: () => {
      toast({ title: "Kund skapad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/clients"] });
      setNewClientDialogOpen(false);
      newClientForm.reset();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte skapa kund.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (clientId: string) => apiRequest("DELETE", `/api/admin/clients/${clientId}`),
    onSuccess: () => {
      toast({ title: "Kund raderad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/clients"] });
      setDeletingClientId(null);
    },
    onError: async (error: any) => {
      const message = error?.message || "Kunde inte radera kund.";
      toast({ title: "Fel", description: message, variant: "destructive" });
      setDeletingClientId(null);
    },
  });

  const suspendMutation = useMutation({
    mutationFn: ({ clientId, isSuspended }: { clientId: string; isSuspended: boolean }) =>
      apiRequest("PATCH", `/api/admin/clients/${clientId}`, { isSuspended }),
    onSuccess: (_, { isSuspended }) => {
      toast({ title: isSuspended ? "Kund spärrad" : "Spärr borttagen" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/clients"] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte ändra spärrningsstatus.", variant: "destructive" });
    },
  });

  const onNewClientSubmit = (data: ClientFormData) => {
    createMutation.mutate(data);
  };

  const openEdit = (client: Client) => {
    setEditingClient(client);
    form.reset({
      name: client.name,
      address: client.address || "",
      phone: client.phone || "",
      email: client.email || "",
      notes: client.notes || "",
      hasRut: client.hasRut ?? false,
      rutDetails: client.rutDetails || "",
      orgNumber: client.orgNumber || "",
      invoiceCadence: client.invoiceCadence === "weekly" || client.invoiceCadence === "monthly" ? client.invoiceCadence : undefined,
      invoiceWeekday: client.invoiceWeekday ?? undefined,
      invoiceTime: client.invoiceTime || "",
      priceListId: client.priceListId || null,
      sellerId: client.sellerId || null,
      communicationPreference: (client.communicationPreference as "email" | "whatsapp" | "sms" | "none") || "email",
    });
    setDialogOpen(true);
  };

  const onSubmit = (data: ClientFormData) => {
    if (editingClient) {
      updateMutation.mutate({ ...data, id: editingClient.id });
    }
  };

  const openCredentials = async (client: Client) => {
    setCredentialsClient(client);
    setExistingUsername(null);
    
    try {
      const response = await fetch(`/api/admin/clients/${client.id}/credentials`, {
        credentials: "include",
      });
      const data = await response.json();
      
      if (data.hasCredentials && data.username) {
        setExistingUsername(data.username);
        credentialsForm.reset({
          username: data.username,
          password: isSuperAdmin ? "" : generatePassword(),
        });
      } else {
        credentialsForm.reset({
          username: "",
          password: generatePassword(),
        });
      }
    } catch {
      credentialsForm.reset({
        username: "",
        password: generatePassword(),
      });
    }
    
    setCredentialsDialogOpen(true);
  };

  const onCredentialsSubmit = (data: CredentialsFormData) => {
    if (credentialsClient) {
      credentialsMutation.mutate({ ...data, clientId: credentialsClient.id });
    }
  };

  const watchHasRut = form.watch("hasRut");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Kunder</h1>
        <p className="text-muted-foreground">Hantera kundprofiler och anteckningar</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <CardTitle>Kundlista</CardTitle>
            <CardDescription>Alla registrerade kunder i systemet</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setRegLinkDialogOpen(true)}
              data-testid="button-registration-link"
            >
              <Link className="h-4 w-4 mr-2" />
              Registreringslänk
            </Button>
            <Button variant="outline" onClick={() => setLocation("/admin/sellers")} data-testid="button-manage-sellers">
              <ShoppingBag className="h-4 w-4 mr-2" />
              Säljare
            </Button>
            <Button onClick={() => setNewClientDialogOpen(true)} data-testid="button-add-client">
              <UserPlus className="h-4 w-4 mr-2" />
              Lägg till kund
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Sök kund (namn, adress, telefon, e-post, org.nr)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="input-search-clients"
            />
          </div>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredClients && filteredClients.length > 0 ? (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Namn</TableHead>
                  <TableHead>Org.nr</TableHead>
                  <TableHead>Adress</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>E-post</TableHead>
                  <TableHead>Rutt</TableHead>
                  <TableHead>Säljare</TableHead>
                  <TableHead>Kommunikation</TableHead>
                  <TableHead className="text-right">Åtgärd</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell className="font-medium">
                      <span>{client.name}</span>
                      {client.isSuspended && (
                        <Badge variant="outline" className="ml-2 bg-red-500/10 text-red-600 border-red-500/20 text-xs">
                          Spärrad
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{client.orgNumber || "-"}</TableCell>
                    <TableCell>{client.address || "-"}</TableCell>
                    <TableCell>{client.phone || "-"}</TableCell>
                    <TableCell>{client.email || "-"}</TableCell>
                    <TableCell>
                      {client.hasRut ? (
                        <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                          Ja
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Nej</Badge>
                      )}
                    </TableCell>
                    <TableCell data-testid={`text-seller-${client.id}`}>
                      {sellers?.find(s => s.id === client.sellerId)?.name || "-"}
                    </TableCell>
                    <TableCell data-testid={`text-comm-pref-${client.id}`}>
                      {(() => {
                        const pref = client.communicationPreference || "email";
                        const labels: Record<string, string> = { email: "E-post", whatsapp: "WhatsApp", sms: "SMS", none: "Ingen" };
                        const colors: Record<string, string> = { email: "bg-blue-50 text-blue-700 border-blue-200", whatsapp: "bg-emerald-50 text-emerald-700 border-emerald-200", sms: "bg-green-50 text-green-700 border-green-200", none: "bg-gray-50 text-gray-500 border-gray-200" };
                        return <Badge variant="outline" className={`text-xs ${colors[pref] || ""}`}>{labels[pref] || pref}</Badge>;
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => suspendMutation.mutate({ clientId: client.id, isSuspended: !client.isSuspended })}
                          disabled={suspendMutation.isPending}
                          title={client.isSuspended ? "Ta bort spärr" : "Spärra kund"}
                          className={client.isSuspended ? "text-green-600" : "text-red-500"}
                          data-testid={`button-suspend-client-${client.id}`}
                        >
                          {client.isSuspended ? <CheckCircle2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openCredentials(client)}
                          title="Inloggningsuppgifter"
                          data-testid={`button-credentials-client-${client.id}`}
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEdit(client)}
                          title="Redigera"
                          data-testid={`button-edit-client-${client.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog open={deletingClientId === client.id} onOpenChange={(open) => !open && setDeletingClientId(null)}>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-destructive"
                              onClick={() => setDeletingClientId(client.id)}
                              title="Radera"
                              data-testid={`button-delete-client-${client.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Radera kund?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Detta kommer att permanent radera {client.name}. Kunder med befintliga ordrar kan inte raderas.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Avbryt</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMutation.mutate(client.id)}
                                disabled={deleteMutation.isPending}
                                className="bg-destructive text-destructive-foreground"
                              >
                                {deleteMutation.isPending ? "Raderar..." : "Radera"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchQuery.trim() ? "Inga kunder matchar din sökning" : "Inga kunder registrerade ännu"}
              </p>
              {!searchQuery.trim() && (
                <p className="text-sm text-muted-foreground mt-2">
                  Kunder skapas automatiskt när de loggar in och lägger en beställning
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg p-0">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle>Redigera kundprofil</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="max-h-[60vh] overflow-y-auto px-4">
                <div className="space-y-4 pb-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Namn</FormLabel>
                    <FormControl>
                      <Input {...field} data-testid="input-client-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Adress</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Gatuadress, postnummer, stad" data-testid="input-client-address" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefon</FormLabel>
                      <FormControl>
                        <Input {...field} type="tel" placeholder="+46701234567" data-testid="input-client-phone" />
                      </FormControl>
                      <FormDescription className="text-xs">Inkl. landskod, t.ex. +46…</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-post</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" data-testid="input-client-email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Anteckningar</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Interna anteckningar om kunden (visas på leveranssedel)"
                        data-testid="input-client-notes"
                      />
                    </FormControl>
                    <FormDescription>Dessa anteckningar visas på leveranssedeln</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hasRut"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Rutt</FormLabel>
                      <FormDescription>Ange vilken leveransrutt kunden tillhör</FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="switch-has-rut"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {watchHasRut && (
                <FormField
                  control={form.control}
                  name="rutDetails"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ruttdetaljer</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="T.ex. bil 1, norra rutten, etc."
                          data-testid="input-rut-details"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="orgNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Org.nr</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="XXXXXX-XXXX" data-testid="input-org-number" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priceListId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prislista</FormLabel>
                    <Select
                      value={field.value || "none"}
                      onValueChange={(val) => field.onChange(val === "none" ? null : val)}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-price-list">
                          <SelectValue placeholder="Standard (ordinarie priser)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Standard (ordinarie priser)</SelectItem>
                        {priceListOptions?.filter(pl => !pl.isDefault).map(pl => (
                          <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>Styr vilka priser kunden ser vid beställning</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sellerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Säljare</FormLabel>
                    <Select
                      value={field.value || "none"}
                      onValueChange={(val) => field.onChange(val === "none" ? null : val)}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-seller">
                          <SelectValue placeholder="Ingen säljare" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Ingen säljare</SelectItem>
                        {sellers?.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="communicationPreference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Föredragen kommunikation</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="select-communication-preference">
                          <SelectValue placeholder="Välj kanal" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="email">E-post</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                        <SelectItem value="none">Ingen</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>Hur följesedeln skickas vid leverans</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-3 rounded-lg border p-3">
                <p className="text-sm font-medium">Fakturering</p>
                <FormField
                  control={form.control}
                  name="invoiceCadence"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Faktureringsperiod</FormLabel>
                      <Select
                        value={field.value || "none"}
                        onValueChange={(val) => field.onChange(val === "none" ? undefined : val)}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-invoice-cadence">
                            <SelectValue placeholder="Välj period" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Ej vald</SelectItem>
                          <SelectItem value="weekly">Veckovis</SelectItem>
                          <SelectItem value="monthly">Månadsvis</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch("invoiceCadence") === "weekly" && (
                  <FormField
                    control={form.control}
                    name="invoiceWeekday"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Faktureringsdag</FormLabel>
                        <Select
                          value={field.value !== undefined ? String(field.value) : ""}
                          onValueChange={(val) => field.onChange(val ? parseInt(val) : undefined)}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-invoice-weekday">
                              <SelectValue placeholder="Välj dag" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1">Måndag</SelectItem>
                            <SelectItem value="2">Tisdag</SelectItem>
                            <SelectItem value="3">Onsdag</SelectItem>
                            <SelectItem value="4">Torsdag</SelectItem>
                            <SelectItem value="5">Fredag</SelectItem>
                            <SelectItem value="6">Lördag</SelectItem>
                            <SelectItem value="0">Söndag</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {form.watch("invoiceCadence") === "monthly" && (
                  <p className="text-xs text-muted-foreground">Månadsvis fakturering genereras automatiskt den sista dagen i varje månad.</p>
                )}

                <FormField
                  control={form.control}
                  name="invoiceTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Faktureringsklockan</FormLabel>
                      <FormControl>
                        <Input {...field} type="time" data-testid="input-invoice-time" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

                </div>
              </div>
              <DialogFooter className="px-4 py-3 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Avbryt
                </Button>
                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                  data-testid="button-save-client"
                >
                  {updateMutation.isPending ? "Sparar..." : "Spara"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* New Client Dialog */}
      <Dialog open={newClientDialogOpen} onOpenChange={setNewClientDialogOpen}>
        <DialogContent className="max-w-lg p-0">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle>Lägg till ny kund</DialogTitle>
            <DialogDescription>
              Skapa en ny kundprofil i systemet
            </DialogDescription>
          </DialogHeader>
          <Form {...newClientForm}>
            <form onSubmit={newClientForm.handleSubmit(onNewClientSubmit)}>
              <div className="max-h-[60vh] overflow-y-auto px-4">
                <div className="space-y-4 pb-4">
              <FormField
                control={newClientForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Namn</FormLabel>
                    <FormControl>
                      <Input {...field} data-testid="input-new-client-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={newClientForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefon</FormLabel>
                      <FormControl>
                        <Input {...field} type="tel" placeholder="+46701234567" data-testid="input-new-client-phone" />
                      </FormControl>
                      <FormDescription className="text-xs">Inkl. landskod, t.ex. +46…</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={newClientForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-post</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" data-testid="input-new-client-email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={newClientForm.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Adress</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Gatuadress, postnummer, stad" data-testid="input-new-client-address" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={newClientForm.control}
                name="orgNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Org.nr</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="XXXXXX-XXXX" data-testid="input-new-org-number" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={newClientForm.control}
                name="priceListId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prislista</FormLabel>
                    <Select
                      value={field.value || "none"}
                      onValueChange={(val) => field.onChange(val === "none" ? null : val)}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-new-price-list">
                          <SelectValue placeholder="Standard (ordinarie priser)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Standard (ordinarie priser)</SelectItem>
                        {priceListOptions?.filter(pl => !pl.isDefault).map(pl => (
                          <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={newClientForm.control}
                name="sellerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Säljare</FormLabel>
                    <Select
                      value={field.value || "none"}
                      onValueChange={(val) => field.onChange(val === "none" ? null : val)}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-new-seller">
                          <SelectValue placeholder="Ingen säljare" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Ingen säljare</SelectItem>
                        {sellers?.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={newClientForm.control}
                name="communicationPreference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Föredragen kommunikation</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="select-new-communication-preference">
                          <SelectValue placeholder="Välj kanal" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="email">E-post</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                        <SelectItem value="none">Ingen</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>Hur följesedeln skickas vid leverans</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

                </div>
              </div>
              <DialogFooter className="px-4 py-3 border-t">
                <Button type="button" variant="outline" onClick={() => setNewClientDialogOpen(false)}>
                  Avbryt
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending}
                  data-testid="button-create-client"
                >
                  {createMutation.isPending ? "Skapar..." : "Skapa kund"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Inloggningsuppgifter</DialogTitle>
            <DialogDescription>
              {existingUsername
                ? isSuperAdmin
                  ? `Du kan ändra både användarnamn och lösenord för ${credentialsClient?.name}.`
                  : `Användarnamn är låst. Du kan endast ändra lösenordet för ${credentialsClient?.name}.`
                : isSuperAdmin
                  ? `Ange användarnamn och lösenord för ${credentialsClient?.name}.`
                  : `Ange användarnamn och lösenord för ${credentialsClient?.name}. Användarnamnet kan inte ändras efteråt.`
              }
            </DialogDescription>
          </DialogHeader>
          <Form {...credentialsForm}>
            <form onSubmit={credentialsForm.handleSubmit(onCredentialsSubmit)} className="space-y-4">
              <FormField
                control={credentialsForm.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Användarnamn</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="t.ex. kund123" 
                        data-testid="input-cred-username"
                        disabled={!!existingUsername && !isSuperAdmin}
                        className={existingUsername && !isSuperAdmin ? "bg-muted" : ""}
                      />
                    </FormControl>
                    {existingUsername && !isSuperAdmin && (
                      <p className="text-xs text-muted-foreground">Användarnamn kan inte ändras</p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={credentialsForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nytt lösenord</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input 
                          {...field} 
                          type="text"
                          placeholder="Minst 6 tecken" 
                          data-testid="input-cred-password"
                          className="font-mono"
                        />
                      </FormControl>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon"
                        onClick={() => credentialsForm.setValue("password", generatePassword())}
                        title="Generera nytt lösenord"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Lösenordet visas endast en gång efter att du sparar</p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCredentialsDialogOpen(false)}>
                  Avbryt
                </Button>
                <Button
                  type="submit"
                  disabled={credentialsMutation.isPending}
                  data-testid="button-save-credentials"
                >
                  {credentialsMutation.isPending ? "Sparar..." : "Spara"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Saved Credentials Confirmation Dialog */}
      <Dialog open={!!savedCredentials} onOpenChange={(open) => !open && setSavedCredentials(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Inloggningsuppgifter sparade</DialogTitle>
            <DialogDescription>
              Inloggningsuppgifter för {savedCredentials?.clientName} har sparats. 
              Kopiera lösenordet nu - det visas endast denna gång!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Användarnamn</label>
              <div className="flex gap-2">
                <Input 
                  value={savedCredentials?.username || ""} 
                  readOnly 
                  className="font-mono bg-muted"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(savedCredentials?.username || "");
                    setCopiedField("username");
                    setTimeout(() => setCopiedField(null), 2000);
                  }}
                >
                  {copiedField === "username" ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Lösenord</label>
              <div className="flex gap-2">
                <Input 
                  value={savedCredentials?.password || ""} 
                  readOnly 
                  className="font-mono bg-muted"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(savedCredentials?.password || "");
                    setCopiedField("password");
                    setTimeout(() => setCopiedField(null), 2000);
                  }}
                >
                  {copiedField === "password" ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setSavedCredentials(null)} data-testid="button-close-credentials">
              Jag har kopierat uppgifterna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Registration Link Dialog */}
      <Dialog open={regLinkDialogOpen} onOpenChange={setRegLinkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registreringslänk för nya kunder</DialogTitle>
            <DialogDescription>
              Dela denna länk med nya kunder så att de kan registrera sig själva.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={`${window.location.origin}/onboarding`}
              className="font-mono text-sm bg-muted"
              data-testid="text-registration-link"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/onboarding`);
                setRegLinkCopied(true);
                setTimeout(() => setRegLinkCopied(false), 2000);
              }}
              data-testid="button-copy-registration-link"
            >
              {regLinkCopied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Sidan är offentlig — alla med länken kan registrera sig. Kunden får ett användarnamn och lösenord direkt på sidan.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegLinkDialogOpen(false)}>
              Stäng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
